import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { MyButton } from '@interstellar-component'

import { RegisterCompanyService } from './service'
import { COMPANY_ID_STORAGE_KEY } from './Context'
import StepMembershipApplicationSubmitSuccessful from './components/StepMembershipApplicationSubmitSuccessful'

const POLL_INTERVAL_MS = 3000
const MAX_ATTEMPTS = 20 // ~60s before falling back to "still processing"

// DocuSign appends ?event=<result> to the return URL. Only treat explicit
// decline/cancel events as a hard stop; for everything else (signing_complete,
// viewing_complete, or anything unexpected) we poll the envelope status, which
// is the source of truth.
const DECLINE_EVENTS = ['decline', 'cancel', 'ttl_expired', 'session_timeout', 'exception']

function CenteredMessage({ title, description, children }) {
  return (
    <section className="w-full h-full flex items-center justify-center">
      <div className="flex flex-col items-center justify-center gap-8 w-full max-w-[360px] px-6">
        <div className="flex flex-col gap-3 items-center text-center">
          <h1 className="text-[30px] font-semibold text-gray-900 leading-[38px] tracking-tight">
            {title}
          </h1>
          {description ? (
            <p className="text-base text-gray-600 leading-normal">{description}</p>
          ) : null}
        </div>
        {children}
      </div>
    </section>
  )
}

/**
 * DocuSign embedded-signing return handler.
 *
 * The backoffice-service step-4 sets the envelope returnUrl to
 * `${APP_URL}/company/:id/enrollment/signing-complete`, so DocuSign redirects
 * here after the signer finishes. DocuSign appends `?event=<result>`. We read
 * that for an immediate decline/cancel, otherwise poll signing-status until it
 * reports `completed` — which, with sequential signing, means the CLIENT has
 * signed (the envelope itself then waits for the CLIK review in the SAW portal
 * and the CLIK countersignature; the webhook also moves the company into the
 * review queue).
 */
export default function SigningComplete() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const nav = useNavigate()
  const event = searchParams.get('event') // signing_complete | decline | cancel | ...

  const [state, setState] = useState('checking') // checking | completed | declined | pending
  // "sequential" (client, then CLIK after review) or "single" (old template).
  const [signingFlow, setSigningFlow] = useState(null)
  const timerRef = useRef(null)

  useEffect(() => {
    // Explicit decline/cancel — no point polling.
    if (event && DECLINE_EVENTS.includes(event)) {
      setState('declined')
      return undefined
    }
    if (!id) {
      setState('pending')
      return undefined
    }

    let cancelled = false
    let attempts = 0

    const poll = async () => {
      try {
        const res = await RegisterCompanyService.signingStatus(id)
        const status = res?.data?.status
        if (cancelled) return
        if (status === 'completed') {
          // Enrollment finished — drop the persisted draft id so the next
          // "New company" starts fresh instead of resuming this completed one.
          localStorage.removeItem(COMPANY_ID_STORAGE_KEY)
          setSigningFlow(res?.data?.signing_flow ?? null)
          setState('completed')
          return
        }
        if (status === 'declined' || status === 'voided') {
          setState('declined')
          return
        }
      } catch {
        // Envelope may not be webhook-updated yet — keep polling.
      }
      if (cancelled) return
      attempts += 1
      if (attempts >= MAX_ATTEMPTS) {
        setState('pending')
        return
      }
      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS)
    }

    poll()

    return () => {
      cancelled = true
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [id, event])

  const backToCompany = () => nav('/company')

  if (state === 'completed') {
    return (
      <StepMembershipApplicationSubmitSuccessful
        onBackToCompanyList={backToCompany}
        signingFlow={signingFlow}
      />
    )
  }

  if (state === 'declined') {
    return (
      <CenteredMessage
        title="Signing Not Completed"
        description="The membership agreement was not signed. Return to the company list and re-initiate signing when ready."
      >
        <div className="w-full">
          <MyButton type="button" color="primary" variant="filled" size="lg" expanded onClick={backToCompany}>
            <span className="text-base font-semibold">Back to List of Company</span>
          </MyButton>
        </div>
      </CenteredMessage>
    )
  }

  if (state === 'pending') {
    return (
      <CenteredMessage
        title="Almost There"
        description="Your signature is being processed. This can take a moment — you will be notified once it's confirmed."
      >
        <div className="w-full">
          <MyButton type="button" color="primary" variant="filled" size="lg" expanded onClick={backToCompany}>
            <span className="text-base font-semibold">Back to List of Company</span>
          </MyButton>
        </div>
      </CenteredMessage>
    )
  }

  return (
    <CenteredMessage
      title="Finalizing Your Signature"
      description="Please wait while we confirm your membership agreement signature…"
    >
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-brand/600"
        aria-hidden
      />
    </CenteredMessage>
  )
}
